class StickyCtaCard extends HTMLElement {
  static refreshInterval = 200;

  constructor() {
    super();

    this.isDismissed = false;
    this.isBeyondTarget = false;
    this.onCloseClick = this.onCloseClick.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onTargetIntersection = this.onTargetIntersection.bind(this);
    this.onLayersChanged = this.onLayersChanged.bind(this);
    this.onFocusChange = this.onFocusChange.bind(this);
    this.refresh = this.refresh.bind(this);
    this.scheduleRefresh = this.scheduleRefresh.bind(this);
  }

  connectedCallback() {
    this.closeButton = this.querySelector('[data-sticky-cta-close]');
    this.storageKey = this.dataset.storageKey;
    this.triggerRatio = parseFloat(this.dataset.triggerRatio) || 1;

    if (this.isStoredAsDismissed()) {
      this.remove();
      return;
    }

    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.onCloseClick);
    }

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.scheduleRefresh);
    document.addEventListener('focusin', this.onFocusChange);
    document.addEventListener('focusout', this.onFocusChange);
    this.observeHideTarget();
    this.observeFloatingLayers();
    this.refresh();
  }

  disconnectedCallback() {
    if (this.closeButton) {
      this.closeButton.removeEventListener('click', this.onCloseClick);
    }

    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.scheduleRefresh);
    document.removeEventListener('focusin', this.onFocusChange);
    document.removeEventListener('focusout', this.onFocusChange);

    if (this.targetObserver) this.targetObserver.disconnect();
    if (this.layerObserver) this.layerObserver.disconnect();
    if (this.pendingRefresh) window.clearTimeout(this.pendingRefresh);
  }

  getHideTarget() {
    const target = this.dataset.hideTarget;
    if (!target) return null;

    if (target.startsWith('.') || target.startsWith('#') || target.startsWith('[')) {
      return document.querySelector(target);
    }

    return document.querySelector(`#shopify-section-${target}, [id$="__${target}"]`);
  }

  observeHideTarget() {
    const target = this.getHideTarget();
    if (!target) return;

    this.targetObserver = new IntersectionObserver(this.onTargetIntersection, { threshold: 0 });
    this.targetObserver.observe(target);
  }

  observeFloatingLayers() {
    this.layerObserver = new MutationObserver(this.onLayersChanged);
    this.layerObserver.observe(document.body, { childList: true, subtree: true });
  }

  onLayersChanged(mutations) {
    const isOwnMutation = mutations.every((mutation) => this.contains(mutation.target));
    if (isOwnMutation) return;

    this.scheduleRefresh();
  }

  onTargetIntersection(entries) {
    entries.forEach((entry) => {
      this.isBeyondTarget = entry.isIntersecting || entry.boundingClientRect.top < 0;
    });

    this.refresh();
  }

  onFocusChange() {
    window.requestAnimationFrame(this.refresh);
  }

  isFillingForm() {
    const active = document.activeElement;
    if (!active || this.contains(active)) return false;

    return Boolean(active.form) && active.matches('input, select, textarea');
  }

  scheduleRefresh() {
    if (this.pendingRefresh) return;

    const sinceLastRefresh = Date.now() - (this.lastRefreshedAt || 0);
    const delay = Math.max(0, StickyCtaCard.refreshInterval - sinceLastRefresh);

    this.pendingRefresh = window.setTimeout(() => {
      this.pendingRefresh = null;
      this.refresh();
    }, delay);
  }

  getFloatingLayers() {
    const own = this.getBoundingClientRect();
    const minimumArea = window.innerWidth * window.innerHeight * 0.01;

    return [...document.querySelectorAll('body *')]
      .filter((element) => {
        if (element === this || this.contains(element) || element.contains(this)) return false;

        const styles = window.getComputedStyle(element);
        if (styles.position !== 'fixed' || styles.visibility === 'hidden' || styles.opacity === '0') return false;

        const rect = element.getBoundingClientRect();
        if (rect.width * rect.height < minimumArea) return false;

        return rect.left < own.right && rect.right > own.left;
      })
      .map((element) => element.getBoundingClientRect());
  }

  getBlockOffset(layers) {
    const anchoredToBottom = layers.filter((rect) => rect.bottom >= window.innerHeight - 4);
    const occupied = anchoredToBottom.reduce((tallest, rect) => Math.max(tallest, window.innerHeight - rect.top), 0);

    return `calc(${Math.round(occupied)}px + 1.6rem)`;
  }

  isCoveredByModal(layers) {
    const own = this.getBoundingClientRect();
    const modalArea = window.innerWidth * window.innerHeight * 0.6;

    return layers.some((rect) => {
      if (rect.width * rect.height < modalArea) return false;

      return rect.top < own.bottom && rect.bottom > own.top;
    });
  }

  isStoredAsDismissed() {
    if (!this.storageKey) return false;

    try {
      return window.sessionStorage.getItem(this.storageKey) === 'true';
    } catch (error) {
      return false;
    }
  }

  storeAsDismissed() {
    if (!this.storageKey) return;

    try {
      window.sessionStorage.setItem(this.storageKey, 'true');
    } catch (error) {
      return;
    }
  }

  onCloseClick() {
    this.isDismissed = true;
    this.classList.remove('sticky-cta-card--visible');
    this.storeAsDismissed();
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('focusin', this.onFocusChange);
    document.removeEventListener('focusout', this.onFocusChange);
    if (this.targetObserver) this.targetObserver.disconnect();
    if (this.layerObserver) this.layerObserver.disconnect();
  }

  refresh() {
    if (this.isDismissed) return;

    this.lastRefreshedAt = Date.now();

    const layers = this.getFloatingLayers();
    const blockOffset = this.getBlockOffset(layers);

    if (blockOffset !== this.blockOffset) {
      this.blockOffset = blockOffset;
      this.style.setProperty('--sticky-cta-card-block-offset', blockOffset);
    }

    const isScrolledPastTrigger = window.scrollY >= window.innerHeight * this.triggerRatio;
    const canShow =
      isScrolledPastTrigger && !this.isBeyondTarget && !this.isFillingForm() && !this.isCoveredByModal(layers);

    this.classList.toggle('sticky-cta-card--visible', canShow);
  }

  onScroll() {
    this.refresh();
  }
}

if (!customElements.get('sticky-cta-card')) {
  customElements.define('sticky-cta-card', StickyCtaCard);
}
